import { Transition } from '@headlessui/react'
import { useState } from 'react'
import {
  HTTP_CAMPAIGN_SLUG_HEADER,
  HTTP_CAMPAIGN_TOKEN_HEADER,
} from '@captaincause/lib/api/headers'
import { getCampaign } from 'api/campaign'
import { getProjects, getProjectsFilters } from 'api/projects'
import { injectI18n } from 'utils/i18n'
import { Give } from './Give'
import { UnveilDiftAnimation } from './UnveilDiftAnimation'
import type {
  Campaign,
  Locales,
  ProjectFilters,
  Project as ProjectType,
} from '@captaincause/types'
import type { NextPageWithLayout } from 'app'
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next'

interface PageProps {
  campaign: Campaign
  campaignSlug: string
  projects: ProjectType[]
  token: string
  selectedProject: string
  filters: ProjectFilters | null
}

export const getServerSideProps: GetServerSideProps<
  PageProps
> = async context => {
  const i18n = await injectI18n(context.locale, [
    'campaign-banner.component',
    'footer.component',
    'give.page',
    'projects.page',
    'metadata',
    'filters.component',
  ])

  let token: string = ''
  const slug = context.params?.slug?.at(0) ?? undefined

  const { project: selectedProject = '' } = context.query as {
    project: string
  }
  if (!slug || Array.isArray(slug)) {
    return { notFound: true }
  }

  try {
    const campaign = await getCampaign({
      headers: [[HTTP_CAMPAIGN_SLUG_HEADER, slug]],
      locale: context.locale as Locales,
    })

    token = campaign.live_token ?? ''

    const projects = await getProjects({
      headers: [[HTTP_CAMPAIGN_TOKEN_HEADER, token]],
      locale: context.locale as Locales,
    })

    const filters = await getProjectsFilters({
      locale: context.locale as Locales,
    })

    const filteredThemes = filters.themes.filter(theme =>
      projects.some(
        project =>
          project.theme1?.theme === theme.theme ||
          project.theme2?.theme === theme.theme,
      ),
    )

    const filteredRegions = filters.regions.filter(region =>
      projects.some(project => {
        const hasRegionTagList =
          project.region_tags &&
          Array.isArray(project.region_tags) &&
          project.region_tags.length !== 0
        if (hasRegionTagList) {
          return project.region_tags?.includes(region.name)
        }

        return project.region_tag === region.name
      }),
    )

    const isProjectIdExist = projects?.find(({ id }) => id === selectedProject)

    return {
      props: {
        campaign,
        campaignSlug: slug,
        projects,
        filters: {
          regions: filteredRegions,
          themes: filteredThemes,
        },
        selectedProject: isProjectIdExist ? selectedProject : '',
        token,
        ...i18n,
      },
    }
  } catch (error: unknown) {
    return { notFound: true }
  }
}

// ---

type InferredPageProps = InferGetServerSidePropsType<typeof getServerSideProps>
const SlugGive: NextPageWithLayout<InferredPageProps> = ({
  campaign: initialCampaign,
  campaignSlug,
  projects,
  token,
  selectedProject,
  filters,
}: InferredPageProps) => {
  const [animationsDone, setAnimationsDone] = useState<string[]>([])
  const [shouldDisplayGive, setDisplayGive] = useState(false)
  const [filteredProjects, setFilteredProjects] =
    useState<ProjectType[]>(projects)
  const [campaign, setCampaign] = useState(initialCampaign)

  const shouldHaveFilter = projects?.length > 10
  if (
    campaign.is_consult ||
    campaign.skip_animation ||
    !campaign.displayed_amount?.amount
  ) {
    return (
      <Give
        campaign={campaign}
        projects={filteredProjects}
        token={token}
        selectedProject={selectedProject}
        filters={filters}
        setProjects={setFilteredProjects}
        shouldHaveFilter={shouldHaveFilter}
      />
    )
  }

  return (
    <>
      <Transition
        show={animationsDone.length !== 2}
        as="main"
        id="main"
        className="relative flex-1 transition-transform duration-500 ease-out data-[closed]:translate-y-[10px] data-[closed]:opacity-0"
        style={{ backgroundColor: campaign.primary_color }}
        afterLeave={() => setDisplayGive(true)}
      >
        <UnveilDiftAnimation
          campaign={campaign}
          campaignSlug={campaignSlug}
          setAnimationsDone={setAnimationsDone}
          onCampaignUpdate={setCampaign}
        />
      </Transition>
      <Transition
        as="div"
        show={shouldDisplayGive}
        className="transition-transform delay-100 duration-500 ease-out data-[closed]:translate-y-[20px] data-[closed]:opacity-0"
      >
        <Give
          campaign={campaign}
          projects={filteredProjects}
          token={token}
          selectedProject={selectedProject}
          filters={filters}
          setProjects={setFilteredProjects}
          shouldHaveFilter={shouldHaveFilter}
        />
      </Transition>
    </>
  )
}

export default SlugGive
